import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Layout, Spin, theme } from 'antd';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import GlobalStyles from './styles/GlobalStyles';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import ProtectedRoute from './components/ProtectedRoute';
import 'antd/dist/reset.css';
import StyleProvider from './components/StyleProvider';
import { ClientProvider } from './components/ClientProvider';
import { useAppConfig } from './context/AppConfigContext';
import useResponsive from './hooks/useResponsive';

const { Header, Content } = Layout;
const ActivateAccount = React.lazy(() => import('./pages/ActivateAccount.js'));
const Clients = React.lazy(() => import('./pages/Clients'));
const ChangeNewsletter = React.lazy(() => import('./pages/ChangeNewsletter'));
const Configuration = React.lazy(() => import('./pages/Configuration'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const DocumentStatuses = React.lazy(() => import('./pages/DocumentStatuses'));
const DocumentTypes = React.lazy(() => import('./pages/DocumentTypes'));
const Expositions = React.lazy(() => import('./pages/Expositions'));
const Expositors = React.lazy(() => import('./pages/Expositors'));
const Locations = React.lazy(() => import('./pages/Locations'));
const Login = React.lazy(() => import('./pages/Login'));
const Messages = React.lazy(() => import('./pages/Messages'));
const Orders = React.lazy(() => import('./pages/Orders'));
const Products = React.lazy(() => import('./pages/Products'));
const Reports = React.lazy(() => import('./pages/Reports'));
const RepresentativesPage = React.lazy(() => import('./pages/RepresentativesPage'));
const RequestPasswordReset = React.lazy(() => import('./pages/RequestPasswordReset'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Trade = React.lazy(() => import('./pages/Trade'));
const TradeDocument = React.lazy(() => import('./pages/TradeDocument'));
const Users = React.lazy(() => import('./pages/Users'));
const Visits = React.lazy(() => import('./pages/Visits'));

function TradeFeatureRoute({ documentKind, editSource, documentId }) {
  const { appConfig, loading } = useAppConfig();
  const featureName = documentKind === 'offer' ? 'tradeOffer' : 'tradeZk';

  if (loading) {
    return <Spin />;
  }

  if (!appConfig.features?.[featureName]) {
    return <Navigate to="/" replace />;
  }

  if (editSource && !appConfig.tradeDocuments?.editingEnabled) {
    return <Navigate to="/trade" replace />;
  }

  return (
    <PrivateRoute>
      <TradeDocument
        documentKind={documentKind}
        editSource={editSource}
        documentId={documentId}
      />
    </PrivateRoute>
  );
}

function TradeEditRoute() {
  const { kind, source, documentId } = useParams();
  if (!['zk', 'offer'].includes(kind) || source !== 'nexo' || !/^\d+$/.test(documentId || '')) {
    return <Navigate to="/trade" replace />;
  }
  return <TradeFeatureRoute documentKind={kind} editSource={source} documentId={documentId} />;
}

function TradeOverviewRoute() {
  const { appConfig, loading } = useAppConfig();

  if (loading) {
    return <Spin />;
  }

  if (!appConfig.features?.tradeDocuments) {
    return <Navigate to="/" replace />;
  }

  return (
    <PrivateRoute>
      <Trade />
    </PrivateRoute>
  );
}

function AppContent() {
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { isMobile } = useResponsive();
  const hideNavAndSidebar = 
    location.pathname === '/login' || 
    location.pathname === '/request-password-reset' || 
    location.pathname.startsWith('/reset-password/') ||
    location.pathname.startsWith('/activate-account/');
  
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  return hideNavAndSidebar ? (
    // Simple layout for login pages
    <Layout style={{ minHeight: '100dvh' }}>
      <Content style={{ margin: 0, padding: 0, width: '100%' }}>
        <div
          style={{
            padding: 0,
            textAlign: 'center',
            background: 'transparent',
            height: '100%'
          }}
        >
          <Routes>
            {/* Login-related routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/request-password-reset" element={<RequestPasswordReset />} />
            <Route path="/activate-account/:token" element={<ActivateAccount />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </Content>
    </Layout>
  ) : (
    // Layout with conditional sidebar/navbar based on device
    <Layout style={{ minHeight: '100dvh' }}>
      <PrivateRoute>
      <a className="skip-to-content" href="#quickfox-main-content">
        Przejdź do treści
      </a>
      {/* Navbar only shown on mobile */}
      {isMobile && (
        <Header 
          style={{
            padding: 0,
            height: 'calc(52px + env(safe-area-inset-top))',
            lineHeight: '52px',
            background: colorBgContainer,
          }}
          className="mobile-header"
        >
          <Navbar />
        </Header>
      )}
      <Layout>
        {/* Sidebar only shown on desktop */}
        {!isMobile && (
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed((value) => !value)}
          />
        )}
        <Content
          id="quickfox-main-content"
          role="main"
          style={{
            //margin: '24px 8px 0',
            paddingLeft: !isMobile ? '10px' : '0',
            paddingRight: !isMobile ? '10px' : '0',        
            paddingTop: !isMobile ? '10px' : '6px', 
            overflow: 'initial',
            flex: '1 1 auto',
            minWidth: 0,
            width: isMobile ? '100%' : 'auto',
            maxWidth: '100%',
            //--marginLeft: !isMobile ? '200px' : '0',
          }}
        >
          <div
            style={{
              padding: isMobile ? 10 : 14,
              textAlign: 'left',
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
              height: 'auto',
              minWidth: 0,
            }}
          >
            <Routes>
              {/* Routes remain unchanged */}
              <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
              <Route path="/messages" element={<PrivateRoute><Messages /></PrivateRoute>} />
              <Route path="/clients" element={<PrivateRoute><Clients /></PrivateRoute>} />
              <Route path="/locations" element={<PrivateRoute><Locations /></PrivateRoute>} />
              <Route path="/products" element={<PrivateRoute><Products /></PrivateRoute>} />
              <Route path="/expositions" element={<PrivateRoute><Expositions /></PrivateRoute>} />
              <Route path="/expositors" element={<PrivateRoute><Expositors /></PrivateRoute>} />
              <Route path="/orders" element={<PrivateRoute><Orders /></PrivateRoute>} />
              <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
              <Route path="/visits" element={<PrivateRoute><Visits /></PrivateRoute>} />
              <Route path="/visits/planner" element={<PrivateRoute><Visits pageMode="planner" /></PrivateRoute>} />
              <Route path="/trade" element={<TradeOverviewRoute />} />
              <Route path="/trade/zk" element={<TradeFeatureRoute documentKind="zk" />} />
              <Route path="/trade/offer" element={<TradeFeatureRoute documentKind="offer" />} />
              <Route path="/trade/:kind/:source/:documentId/edit" element={<TradeEditRoute />} />
             
              {/* Trasy wymagające uprawnień administracyjnych */}
              <Route path="/settings" element={<ProtectedRoute requiredRoles={['admin']}><Settings /></ProtectedRoute>} />
              <Route path="/settings/users" element={<ProtectedRoute requiredRoles={['admin']}><Users /></ProtectedRoute>} />
              <Route path="/settings/document-types" element={<ProtectedRoute requiredRoles={['admin']}><DocumentTypes /></ProtectedRoute>} />
              <Route path="/settings/document-statuses" element={<ProtectedRoute requiredRoles={['admin']}><DocumentStatuses /></ProtectedRoute>} />
              <Route path="/settings/change-newsletter" element={<ProtectedRoute requiredRoles={['admin']}><ChangeNewsletter /></ProtectedRoute>} />
              <Route path="/settings/configuration" element={<ProtectedRoute requiredRoles={['admin']}><Configuration /></ProtectedRoute>} />
              <Route path="/representatives" element={<ProtectedRoute requiredRoles={['admin']}><RepresentativesPage /></ProtectedRoute>} />
              <Route path="/representatives/plans" element={<ProtectedRoute requiredRoles={['admin']}><RepresentativesPage defaultKey="plans" /></ProtectedRoute>} />
              <Route path="/representatives/hierarchy" element={<ProtectedRoute requiredRoles={['admin']}><RepresentativesPage defaultKey="hierarchy" /></ProtectedRoute>} />
              <Route path="/representatives/settings" element={<ProtectedRoute requiredRoles={['admin']}><RepresentativesPage defaultKey="settings" /></ProtectedRoute>} />




              {/* Trasy dla niezalogowanych użytkowników */}
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/activate-account/:token" element={<ActivateAccount />} />
              <Route path="/request-password-reset" element={<RequestPasswordReset />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </div>
        </Content>
      </Layout>
      </PrivateRoute>
    </Layout>
  );
}


function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <StyleProvider>
          <GlobalStyles />
          <ClientProvider>
            <div>
              <React.Suspense fallback={<div className="loading-styles"><Spin size="large" /></div>}>
                <AppContent />
              </React.Suspense>
            </div>
          </ClientProvider>
        </StyleProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
